// ---------------------------------------------------------------------------
// Centralised MSAL auth hook.
//
// No page component should ever call `acquireTokenSilent` directly — this
// hook is the single gateway for token acquisition and sign-out.
// ---------------------------------------------------------------------------

import type { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';

import { dbClearAll } from '../../api/sigsDb';
import { createAuthRedirectState } from '../../auth/authRedirectState';
import { getDatalakePca } from '../../auth/datalakeAccount';
import { apiRequest, graphRequest } from '../../auth/msalConfig';
import { STORAGE_KEYS } from '../../constants';

export interface UseAuthReturn {
  account: AccountInfo | null;
  getToken: () => Promise<string>;
  getGraphToken: () => Promise<string>;
  signOut: () => Promise<void>;
}

// Upper bound on a single silent-token attempt. The normal MSAL flow returns
// cached tokens, exchanges a refresh token, then uses a hidden iframe when the
// refresh token must be replaced. The timeout remains as a final escape for
// network or identity-provider failures.
const SILENT_TOKEN_TIMEOUT_MS = 6_000;

export function useAuth(): UseAuthReturn {
  const { instance, accounts } = useMsal();

  const acquireToken = useCallback(
    async (
      scopes: string[],
      allowRedirectFallback: boolean,
    ): Promise<string> => {
      // Read accounts from `instance` at call time, NOT from the captured
      // `accounts` array. msal-react bumps the `accounts` reference on every
      // TOKEN_CACHE_UPDATED event (including after a successful
      // acquireTokenSilent), so capturing it in the deps would re-create
      // `getToken` after every fetch — cascading through every hook that
      // memos a service on it (`useSyntheticJob`, `useRunBlobsText`, …) and
      // re-firing their useEffects forever.
      const account =
        instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!account) throw new Error('No signed-in account');

      // apiRequest targets api://<workflowApiServiceAppId>/access_as_user.
      // This scope requires admin consent in this tenant — do NOT catch a
      // *thrown* error and retry interactively here, as a consent-required
      // failure would loop the user into an admin-approval wall. Consent
      // failures must bubble to the error boundary unchanged.
      //
      // Thrown errors still bubble unchanged. In particular, consent failures
      // must not start an interactive retry because the Workflow API scope
      // requires tenant admin consent.
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<never>((_, reject) => {
        timeoutHandle = setTimeout(
          () => reject(new Error('silent-token-timeout')),
          SILENT_TOKEN_TIMEOUT_MS,
        );
      });

      try {
        const { accessToken } = await Promise.race([
          instance.acquireTokenSilent({ scopes, account }),
          timeout,
        ]);
        return accessToken;
      } catch (err) {
        if (err instanceof Error && err.message === 'silent-token-timeout') {
          if (!allowRedirectFallback) {
            throw err;
          }

          // Initiate the top-level navigation to login.microsoftonline.com.
          // acquireTokenRedirect resolves the moment the URL change is
          // committed, but the actual page unload happens a few microtasks
          // later. If we rethrew here, every caller would see getToken()
          // reject in that window and flash an error state before the page
          // goes away. Returning a never-resolving promise instead pins
          // callers in their current pending state (spinner, etc.) until
          // the unload happens.
          await instance.acquireTokenRedirect({
            scopes,
            account,
            state: createAuthRedirectState(window.location),
          });
          return new Promise<string>(() => {
            /* never resolves — page is navigating away */
          });
        }
        throw err;
      } finally {
        if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      }
    },
    [instance],
  );

  const getToken = useCallback(
    () => acquireToken(apiRequest.scopes, true),
    [acquireToken],
  );

  const getGraphToken = useCallback(
    // Graph data enriches the UI with profile photos and display names.
    // Callers fall back to initials or IDs when Graph auth is unavailable.
    () => acquireToken(graphRequest.scopes, false),
    [acquireToken],
  );

  const signOut = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEYS.SIGS_SAVED_CONFIGS);
    await dbClearAll();
    const datalakePca = getDatalakePca();
    if (datalakePca !== instance) {
      // Best effort: the DataLake cache is tab-scoped sessionStorage, so a
      // failure here must not block signing out of the app.
      try {
        await datalakePca.initialize();
        await datalakePca.clearCache();
      } catch (error) {
        console.warn('Failed to clear the DataLake session', error);
      }
    }
    void instance.logoutRedirect();
  }, [instance]);

  return {
    account: accounts[0] ?? null,
    getToken,
    getGraphToken,
    signOut,
  };
}
